import { isAxiosError } from 'axios';
import { posClient } from './client';
import {
  normalizeCategory,
  normalizeProduct,
  wireBarcodeResponseSchema,
  wireCategoriesIndexSchema,
  wireCategorySchema,
  wireGroupedMenuSchema,
  wireProductSchema,
  wireProductsIndexSchema,
  type Category,
  type MenuData,
  type Product,
  type ProductsPage,
} from '@/types/products';

export interface ProductRequestOptions {
  /** Register reads use the authenticated terminal’s stock warehouse. */
  salesChannel?: 'pos';
  signal?: AbortSignal;
}

/**
 * GET {pos}/products params (ProductController.php:83-145).
 *
 * `featured` is DELIBERATELY not expressible here: the controller filters a
 * nonexistent `featured` column and 500s (ProductController.php:119 vs the
 * model's real `is_featured`). "Top" derives client-side from `is_featured`.
 * Params are serialized through an explicit allow-list so no stray key can
 * ever reach the query string.
 */
export interface ProductListParams {
  search?: string;
  /** Ecommerce register: server filters individual variants before pagination. */
  variant_search?: boolean;
  category_id?: number;
  taxonomy_category_uid?: string;
  /** Default/'all' includes every lifecycle state; sell screens pass 'active'. */
  status?: 'active' | 'inactive' | 'archived' | 'all';
  /** true → addon products only; false → menu products only. */
  is_addon?: boolean;
  sort?: 'low_to_high' | 'high_to_low';
  page?: number;
  per_page?: number;
}

function serializeListParams(params: ProductListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.search) out.search = params.search;
  if (params.variant_search) out.variant_search = 1;
  if (params.category_id != null) out.category_id = params.category_id;
  if (params.taxonomy_category_uid) out.taxonomy_category_uid = params.taxonomy_category_uid;
  if (params.status) out.status = params.status;
  if (params.is_addon !== undefined) out.is_addon = params.is_addon ? 1 : 0;
  if (params.sort) out.sort = params.sort;
  if (params.page != null) out.page = params.page;
  if (params.per_page != null) out.per_page = params.per_page;
  return out;
}

export interface CategoryListParams {
  status?: 'active' | 'inactive' | 'all';
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export const productsApi = {
  /**
   * GET {pos}/products — `{ data, pagination, last_sync }` (custom envelope).
   * Server-side search matches a parent product's name, slug or SKU, plus
   * child variant SKUs. Variant matches resolve to their parent product so
   * catalog pagination and grouping remain stable.
   */
  async list(
    params: ProductListParams,
    options: ProductRequestOptions = {},
  ): Promise<ProductsPage> {
    const res = await posClient.get('/products', {
      params: { ...serializeListParams(params), sales_channel: options.salesChannel },
      signal: options.signal,
    });
    const parsed = wireProductsIndexSchema.safeParse(res.data);
    if (!parsed.success) {
      // Keep production diagnostics actionable without logging the catalog or
      // customer data: the paths identify the backend field that drifted.
      console.error(
        '[api] products index response schema mismatch',
        parsed.error.issues.map(({ path, message }) => ({ path: path.join('.'), message })),
      );
      throw parsed.error;
    }
    const body = parsed.data;
    return {
      products: body.data.map(normalizeProduct),
      page: body.pagination.current_page,
      lastPage: body.pagination.last_page,
      perPage: body.pagination.per_page,
      total: body.pagination.total,
      lastSync: body.last_sync,
    };
  },

  /**
   * GET {pos}/products?groupBy=category — active categories (by sort_order)
   * with nested active non-addon products, plus the flat addon pool. No
   * pagination; search/category/status params are ignored in this mode.
   */
  async groupedMenu(options: ProductRequestOptions = {}): Promise<MenuData> {
    const res = await posClient.get('/products', {
      params: { groupBy: 'category', sales_channel: options.salesChannel },
      signal: options.signal,
    });
    const body = wireGroupedMenuSchema.parse(res.data);
    return {
      categories: body.data.map(normalizeCategory),
      addons: body.addons.map(normalizeProduct),
      lastSync: body.last_sync,
    };
  },

  /**
   * GET {pos}/products/{id} — the bare model, NOT an envelope
   * (ProductController.php:168-175 returns `response()->json($product)`).
   *
   * Added for the order editor (V2.3-01). Seeding an editor means resolving
   * every line's product against the LIVE catalog, and on a large menu the
   * store is in paged mode — an order placed last Tuesday can easily contain a
   * product that is on no currently loaded page. Without this the editor would
   * either drop those lines or price them from a snapshot, and both of those
   * are ways to silently change an order the operator only meant to look at.
   *
   * Returns null on 404 so a withdrawn product reads as "gone" rather than as
   * a failure to load the order.
   */
  async byId(productId: number, options: ProductRequestOptions = {}): Promise<Product | null> {
    try {
      const res = await posClient.get(`/products/${productId}`, {
        signal: options.signal,
        params: { sales_channel: options.salesChannel },
      });
      return normalizeProduct(wireProductSchema.parse(res.data));
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  },

  /**
   * GET {pos}/products/barcode/{barcode} — exact active-sku match (any type,
   * including variant rows). Returns null on the endpoint's 404
   * `{ error: "Product not found" }`; other errors rethrow.
   */
  async byBarcode(
    barcode: string,
    options: ProductRequestOptions = {},
  ): Promise<{
    product: Product;
    stockAvailable: number | null;
    barcodeMatch: 'sku' | 'gtin' | 'alias' | 'supplier_sku' | null;
    unitQuantity: number;
  } | null> {
    try {
      const res = await posClient.get(`/products/barcode/${encodeURIComponent(barcode)}`, {
        params: { sales_channel: options.salesChannel },
        signal: options.signal,
      });
      const body = wireBarcodeResponseSchema.parse(res.data);
      return {
        product: normalizeProduct(body.product),
        stockAvailable: body.stock_available ?? null,
        barcodeMatch: body.barcode_match ?? null,
        unitQuantity: Number(body.unit_quantity ?? 1) || 1,
      };
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  },

  /**
   * GET {pos}/categories — raw Laravel paginator. Used for chips when the
   * catalog is too large for the grouped menu payload.
   */
  async categories(
    params: CategoryListParams = {},
    options: ProductRequestOptions = {},
  ): Promise<{ categories: Category[]; total: number; lastPage: number }> {
    const res = await posClient.get('/categories', { params, signal: options.signal });
    const body = wireCategoriesIndexSchema.parse(res.data);
    return {
      categories: body.data.map(normalizeCategory),
      total: body.total,
      lastPage: body.last_page,
    };
  },

  /** GET /categories/{id} — resolve a saved picker selection without loading every category. */
  async categoryById(categoryId: number, options: ProductRequestOptions = {}): Promise<Category> {
    const res = await posClient.get(`/categories/${categoryId}`, { signal: options.signal });
    return normalizeCategory(wireCategorySchema.parse(res.data));
  },
};
